import { Dayjs } from 'dayjs';
import dayjsFunc = require('dayjs');
import { List, Map, Set } from 'immutable';
import * as Immutable from 'immutable';
import { connect, Store } from 'react-redux';
import { ParametricSelector, Selector as ReselectSelector } from 'reselect';

import { assert, buildService, equalsIgnoringCase } from '../utils';

export { Store } from 'react-redux';
export { createSelector, createStructuredSelector } from 'reselect';
/**
 * Opinionated redux implementation for this app
 */

// For the generic definitions below
// tslint:disable: no-any
export interface IAction<T = any> {
  type: string;
  payload?: T;
  ngAdminAppMessage?: boolean;
  error?: boolean;
}

export type ActionCreator<T extends any[]> = (...args: T) => Dispatchable;

export type Selector<S, R, P> = ReselectSelector<S, R> | ParametricSelector<S, P, R>;

type Thunk<S = any, R = any> = (dispatch: MDispatch, getState: () => S) => Promise<R> | void;
export type Dispatchable<S = any, R = any> = Thunk<S, R> | IAction;
export type MDispatch<S = any, R = any> = (dispatchable: Dispatchable<S, R>) => Promise<R>;

export interface IDispatchProps {
  dispatch: MDispatch;
}

export type ImmutableDate = Dayjs;
export const createImmutableDate = dayjsFunc;

export type ImmutableTypes =
  | string
  | number
  | boolean
  | undefined
  | null
  | ImmutableDate
  | IImmutableMap<any>
  | IImmutableWeakTypedMap
  | IImmutableList<any>
  | IImmutableSet<any>;

export type ImmutablePropsMap<T> = { [K in keyof T]: T[K] extends ImmutableTypes ? T[K] : never };

export type ReducerFunction<S extends ImmutableTypes> = (state: S, action: IAction) => S;
export type InferReducerFunction<T> = T extends ReducerFunction<infer K> ? K : never;
export type InferReducer<T> = T extends ReducerGraphNode<infer R> ? R : InferReducerFunction<T>;

export type ReducerMap<T> = {
  [K in keyof T]: ReducerGraphNode<any> | ReducerFunction<ImmutableTypes>;
};

export interface IImmutableMap<T extends ImmutablePropsMap<T>> extends Map<keyof T, any> {
  get<K extends keyof T>(key: K): T[K];
  set<K extends keyof T>(key: K, value: T[K]): IImmutableMap<T>;
  withMutations(cb: (r: IImmutableMap<T>) => void): IImmutableMap<T>;
  toObject(): T;
}

export type InferImmutableMap<T> = T extends IImmutableMap<infer K> ? K : never;

export interface IImmutableWeakTypedMap<T extends ImmutableTypes = any> extends Map<string, T> {
  [x: string]: any;
  get(key: string): T;
  set(key: string, value: T): IImmutableWeakTypedMap<T>;
  withMutations(cb: (r: IImmutableWeakTypedMap<T>) => void): IImmutableWeakTypedMap<T>;
  toObject(): any;
}

export type InferImmutableWeakMap<T> = T extends IImmutableWeakTypedMap<infer K> ? K : never;

export interface IImmutableList<T extends ImmutableTypes> extends List<T> {}

export type InferImmutableList<T> = T extends IImmutableList<infer K> ? K : never;

export interface IImmutableSet<T extends ImmutableTypes> extends Set<T> {}

export type InferImmutableSet<T> = T extends IImmutableSet<infer K> ? K : never;

export const createImmutableMap = Map as <T extends ImmutablePropsMap<T>>(data: T) => IImmutableMap<T>;

export const createImmutableWeakTypedMap = Map as <T extends ImmutableTypes = any>(
  data: any
) => IImmutableWeakTypedMap<T>;

export const createImmutableList = List as <T extends ImmutableTypes>(data: T[]) => IImmutableList<T>;

export const createImmutableSet = Set as <T extends ImmutableTypes>(data: T[]) => IImmutableSet<T>;

/**
 * Create a reducer dedicated for one field in the AppState, which specifies all reducers
 * for all revelent actions for this field via the {@param handlers} map.
 *
 * @param initialValue initial value of this field
 * @param handlers A map of actionType => reducers for the field
 */
export function createStateField<S extends ImmutableTypes>(
  initialValue: S,
  handlers: { [key: string]: ReducerFunction<S> }
): ReducerFunction<S> {
  return (state: S, action: IAction): S => {
    if (state === undefined) {
      state = initialValue;
    }
    /**
     * On Windows, when a case-only rename is done to a file that contains `__filename` and the file is accessed from webpack.
     * The `__filename` value we got back is non-deterministic. This throw off state management system and reducers will not
     * be triggering correctly.
     *
     * Meanwhile, (1) normalizing all action type to upper/lower case will make the readability in debugging tools suck.
     * Doing a (2) case-insensitive comparison hurts performance in production as action firing is performance sensitive.
     * So I decided to do (2) in development build, and (1) in production build.
     */

    if (buildService.isDevelopment) {
      const actionFound = Object.keys(handlers).filter((key: string) => equalsIgnoringCase(key, action.type));
      if (actionFound.length === 1) {
        const actionType = actionFound[0];
        return handlers[actionType](state, action);
      } else {
        return state;
      }
    } else {
      if (handlers.hasOwnProperty(action.type)) {
        return handlers[action.type](state, action);
      } else {
        return state;
      }
    }
  };
}

/**
 * Generate global unique, while human-readable actionType strings.
 * Always, always use `__filename` for {@param suffix}
 *
 * @param map A map of actionName => stringValue
 * @param suffix Please always use __filename for this parameter. A common suffix for this set of actions
 */
export function createActionTypes<S extends { [key: string]: string }>(map: S, suffix: string): Readonly<S> {
  if (!buildService.isProduction) {
    const values = Object.values(map).sort();
    for (let i = 0; i < values.length - 1; i++) {
      if (values[i + 1] === values[i]) {
        throw new Error(`Duplicate action values in ${suffix}`);
      }
    }
  }
  Object.keys(map).forEach((key: keyof S) => {
    // See `createStateField()` for explaination
    if (buildService.isDevelopment) {
      map[key] = `${map[key]} (${suffix})` as any;
    } else {
      map[key] = `${map[key]} (${suffix.toUpperCase()})` as any;
    }
  });
  return map;
}

function getStateName(action: IAction): string {
  return action && action.type === '@@redux/INIT'
    ? 'initialState argument passed to createStore'
    : 'previous state received by the reducer';
}

function validateNextState(nextState: ImmutableTypes, reducerName: string, action: IAction): void {
  if (nextState === undefined) {
    throw new Error(
      `Reducer ${reducerName} returned undefined when handling ${action.type} action.
      To ignore an action, you must explicitly return the previous state.`
    );
  }
}

function getUnexpectedInvocationParameterMessage(
  state: IImmutableMap<any>,
  reducers: ReducerMap<any>,
  action: IAction
): string | null {
  const reducerNames = Object.keys(reducers);

  if (!reducerNames.length) {
    return 'Store does not have a valid reducer. Make sure the argument passed to combineReducers is an object whose values are reducers.';
  }

  const stateName = getStateName(action);

  if (!Immutable.Iterable.isIterable(state)) {
    return `The ${stateName} is of unexpected type.
    Expected argument to be an instance of Immutable.Collection or Immutable.Record with the following properties:
    "${reducerNames.join('", "')}".`;
  }

  const unexpectedStatePropertyNames = state
    .toSeq()
    .keySeq()
    .toArray()
    .filter((name: string) => {
      return !reducers.hasOwnProperty(name);
    });

  if (unexpectedStatePropertyNames.length > 0) {
    return `Unexpected properties: "${unexpectedStatePropertyNames.join('", "')}" found in ${stateName}.
    Expected to find one of the known reducer property names instead: "${reducerNames.join('", "')}".
    Unexpected properties will be ignored.`;
  }

  return null;
}

function iterateReducerMap<S>(reducers: ReducerMap<S>, iterator: (name: string, reducer: any) => void): void {
  const reducerKeys = Object.keys(reducers);

  reducerKeys.forEach((reducerName: string) => {
    const reducer = (reducers as any)[reducerName];
    iterator(reducerName, reducer);
  });
}

export class ReducerGraphNode<S extends ImmutablePropsMap<S>> {
  /**
   * Note that we don't want to support removeSubStates since we only intend to get better splitting.
   *
   * Otherwise making reducers too dynamic is inviting problem. If there's a use-case where
   * removing reducer is needed, use an alternative way.
   */

  private dynamicReducers: Object = {};
  private parentNode?: ReducerGraphNode<any> = undefined;
  private parentKeyName?: string = undefined;

  constructor(
    private readonly staticReducers: ReducerMap<Partial<S>>,
    private readonly getDefaultState: Function = Immutable.Map,
    private readonly getStore?: () => Store<any>
  ) {
    this._setParent(staticReducers);
  }
  public addSubStates(newReducers: ReducerMap<Partial<S>>): void {
    this.dynamicReducers = {
      ...this.dynamicReducers,
      ...(newReducers as object)
    };
    this._setParent(this.dynamicReducers as any);
    if (this.parentNode !== undefined && this.parentKeyName !== undefined) {
      this.parentNode.addSubStates({ [this.parentKeyName]: this });
    } else if (!buildService.isTest) {
      assert(this.getStore!() !== undefined, 'rootReudcer should have reference to store');
      this.getStore!().replaceReducer(this.getReducerFunction());
    }
  }
  public getReducerFunction(): ReducerFunction<IImmutableMap<S>> {
    // Note that the returned type here is a super set of the actual runtime type, but more valid in coding-time
    const reducers = {
      ...(this.staticReducers as object),
      ...this.dynamicReducers
    } as ReducerMap<Partial<S>>;

    return (inputState: IImmutableMap<S>, action: IAction): IImmutableMap<S> => {
      if (inputState === undefined) {
        inputState = this.getDefaultState();
      }
      if (process.env.NODE_ENV !== 'production') {
        const warningMessage = getUnexpectedInvocationParameterMessage(inputState, reducers, action);

        if (warningMessage) {
          console.error(warningMessage);
        }
      }

      return inputState.withMutations((temporaryState: IImmutableMap<any>) => {
        iterateReducerMap(reducers, (reducerName: string, reducer: any) => {
          const currentDomainState = temporaryState.get(reducerName as any);
          const reducerFunction = reducer instanceof ReducerGraphNode ? reducer.getReducerFunction() : reducer;
          const nextDomainState = reducerFunction(currentDomainState, action, inputState);

          validateNextState(nextDomainState, reducerName, action);

          temporaryState.set(reducerName as any, nextDomainState);
        });
      });
    };
  }

  private _setParent(reducers: ReducerMap<Partial<S>>): void {
    iterateReducerMap(reducers, (name: string, reducer: any) => {
      if (reducer instanceof ReducerGraphNode) {
        reducer.parentKeyName = name;
        reducer.parentNode = this;
      }
    });
  }
}

/**
 * Combines a set of states generated by {@function createStateField()} together
 * to form a state hierarchy
 *
 * @param reducers A map of reducerName => reducer
 */
export function combineStates<S extends ImmutablePropsMap<S> = never>(
  reducers: ReducerMap<Partial<S>>,
  getDefaultState: Function = Immutable.Map,
  getStore?: () => Store<any>
): ReducerGraphNode<S> {
  return new ReducerGraphNode<S>(reducers, getDefaultState, getStore);
}

/**
 * Connect a react component with redux store
 */
export const mconnect = connect;
